import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import useSheetDrag, { findScroller } from '../../hooks/useSheetDrag';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useStatusBarDim } from '../../lib/statusBarColor';

// Gap left between a revealed field and the bottom of what scrolls it.
const REVEAL_MARGIN = 12;

/**
 * Scrolls the focused field back into view inside its sheet. Lifting the sheet
 * shrinks its scrolling area, which pushes a low field (the session comment)
 * under the keyboard, and the browser cannot help: its own reveal scrolls the
 * page, which the sheet undoes. Not scrollIntoView, which would move the page.
 */
const revealFocusedField = () => {
  const field = document.activeElement;
  if (!field?.matches('input, textarea')) return;
  const sheet = field.closest('[role="dialog"]');
  const scroller = sheet && findScroller(field.parentElement, sheet);
  if (!scroller) return;
  const hidden = field.getBoundingClientRect().bottom + REVEAL_MARGIN - scroller.getBoundingClientRect().bottom;
  if (hidden > 0) scroller.scrollTop += hidden;
};

/**
 * Bottom-sheet shell shared by the student overlays (profile, notifications).
 *
 * Extracted verbatim from StudentProfileSheet, which owned this markup alone
 * until a second sheet needed it. It is deliberately *not* built on <BaseModal>
 * or <ContainedSideSheet>: neither is a bottom sheet (BaseModal is
 * centre-anchored with a `min-w-[min(500px,...)]` floor, ContainedSideSheet
 * slides in from the right), and the only other bottom sheet in the repo is
 * private to StudentRankings.jsx.
 *
 * Contract kept identical to the original implementation:
 *  - `createPortal` into <body>, matching the app's overlay convention;
 *  - dimmed backdrop that closes on click, Escape closes too — and an open
 *    sheet OWNS Escape (capture phase, `preventDefault`): the surface it was
 *    opened from (a modal, the video detail overlay) must not close with it;
 *  - `zClassName` sets the stacking level (default `z-[70]`, above the bottom
 *    nav); a sheet opened from inside another modal passes a higher one;
 *  - on iOS the software keyboard does not resize the layout viewport, so a
 *    `position: fixed; bottom: 0` sheet stays put and its field ends up
 *    under the keyboard; the sheet is lifted by the visual viewport's
 *    shortfall while a field inside it has focus, and that field is scrolled
 *    back into view inside the sheet;
 *  - `role="dialog"` + `aria-modal`, named by `labelledBy` (an id rendered by
 *    the caller inside `children`) or by `ariaLabel`;
 *  - body scroll locked while open, always released on unmount;
 *  - rounded 27px top, centred drag handle;
 *  - bottom padding adds `env(safe-area-inset-bottom)` so the last control does
 *    not sit under the iPhone home indicator (iOS Safari) — Android Chrome
 *    resolves the inset to 0px and simply keeps the 20px.
 *
 * Open/close are animated (slide up from the bottom + backdrop fade), so the
 * component stays mounted while closed and AnimatePresence drives the
 * unmounting — callers must NOT gate their own render on `isOpen`, or the exit
 * animation is cut short.
 *
 * From 1024px (computer, landscape tablet) the same content opens as a centred
 * dialog instead: no drag handle, no swipe, a short fade. Below that width the
 * sheet is exactly the phone one.
 *
 * Swiping the sheet down closes it (see useSheetDrag). `expandable` sheets —
 * long lists — can also be swiped up to take more of the screen; their capped
 * inner list should lift its cap with `[[data-sheet-resized]_&]:max-h-none`.
 */
const BottomSheet = ({
  isOpen,
  onClose,
  labelledBy,
  ariaLabel,
  testId,
  backdropTestId,
  className = '',
  zClassName = 'z-[70]',
  expandable = false,
  children,
}) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const sheetRef = useSheetDrag({ onClose, expandable, enabled: !isDesktop });
  // The backdrop darkens the page; the Android status bar must follow.
  useStatusBarDim(isOpen);

  // Escape closes — and is consumed here, so a host that also listens for it
  // (ExerciseValidationModal, StudentVideoDetailOverlay) stays open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  // Keyboard lift: while the sheet is open, follow the visual viewport. iOS
  // and Chrome Android (resizes-visual since v108) both leave the layout
  // viewport alone when the keyboard opens.
  //
  // The page behind is pinned at the scroll position it had on open. To reveal
  // a focused field, the browser scrolls the page under the keyboard, but the
  // body is overflow-locked so nothing legitimate can scroll it here. Left
  // alone, that shift desynchronises the fixed sheet from what iOS paints (the
  // caret is drawn a line below the text), and it outlives the sheet, so the
  // screen stays shifted after it closes. It is undone as soon as it happens
  // and again on close.
  const [keyboardLift, setKeyboardLift] = useState(0);
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return undefined;
    const lockedScrollY = window.scrollY;
    const unpan = () => {
      if (window.scrollY !== lockedScrollY) window.scrollTo(0, lockedScrollY);
    };
    const vv = window.visualViewport;
    const update = () => {
      unpan();
      if (!vv) return;
      const shortfall = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardLift(shortfall > 40 ? shortfall : 0);
    };
    update();
    window.addEventListener('scroll', unpan);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('scroll', unpan);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      setKeyboardLift(0);
      unpan();
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (keyboardLift) revealFocusedField();
  }, [keyboardLift]);

  // Lock the page behind the sheet, and always release on unmount.
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <React.Fragment key="student-bottom-sheet">
          {/* Backdrop */}
          <motion.div
            data-testid={backdropTestId}
            className={`fixed inset-0 ${zClassName} bg-black/60 backdrop-blur-sm`}
            onClick={onClose}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Sheet. x:'-50%' carries the horizontal centring the class-based
              -translate-x-1/2 used to provide — framer-motion owns the whole
              transform, so the class would be overwritten. */}
          <motion.div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : ariaLabel}
            data-testid={testId}
            // Keyboard already up: going to another field must not hide it.
            onFocus={keyboardLift ? revealFocusedField : undefined}
            className={isDesktop
              ? `fixed left-1/2 top-1/2 ${zClassName} flex max-h-[85dvh] w-[calc(100%-48px)] max-w-[480px] flex-col overflow-y-auto rounded-[27px] pt-[14px] ${className}`
              : `fixed bottom-0 left-1/2 ${zClassName} flex w-full max-w-[640px] flex-col rounded-t-[27px] ${className}`}
            style={isDesktop ? {
              background: '#0e0e10',
              border: '0.5px solid rgba(255,255,255,0.12)',
              paddingBottom: 20,
            } : {
              background: '#0e0e10',
              borderTop: '0.5px solid rgba(255,255,255,0.12)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
              // Above the keyboard, and capped so the sheet's own content never
              // runs off the top of the screen.
              bottom: keyboardLift,
              maxHeight: keyboardLift ? `calc(100dvh - ${keyboardLift}px)` : undefined,
            }}
            initial={isDesktop ? { x: '-50%', y: '-46%', opacity: 0 } : { x: '-50%', y: '100%' }}
            animate={isDesktop ? { x: '-50%', y: '-50%', opacity: 1 } : { x: '-50%', y: 0 }}
            exit={isDesktop ? { x: '-50%', y: '-46%', opacity: 0 } : { x: '-50%', y: '100%' }}
            transition={isDesktop ? { duration: 0.18, ease: [0.32, 0.72, 0, 1] } : { type: 'spring', stiffness: 380, damping: 38 }}
          >
            {/* Drag handle — phones only: the dialog has nothing to drag. */}
            {!isDesktop && (
              <div className="flex shrink-0 justify-center pt-3 pb-1">
                <div className="h-1 w-10 rounded-full bg-kaiylo-txt-muted" />
              </div>
            )}

            {children}
          </motion.div>
        </React.Fragment>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BottomSheet;
